(() => {
  if (window.__elagiliNativeCardCartLoaded) return;
  window.__elagiliNativeCardCartLoaded = true;

  let cartQuantities = new Map();
  let cartLineKeys = new Map();
  const busyVariants = new Set();

  const rebuildCartMaps = (cart) => {
    cartQuantities = new Map();
    cartLineKeys = new Map();

    for (const item of cart?.items || []) {
      const variantId = String(item.variant_id);
      cartQuantities.set(variantId, (cartQuantities.get(variantId) || 0) + Number(item.quantity || 0));
      if (!cartLineKeys.has(variantId) && item.key) cartLineKeys.set(variantId, item.key);
    }
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Cart read failed: HTTP ${response.status}`);
    const cart = await response.json();
    rebuildCartMaps(cart);
    return cart;
  };

  const emitCartUpdate = (cart, variantId) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          sourceId: 'elagili-native-card-cart',
          data: {
            source: 'elagili-native-card-cart',
            variantId,
            itemCount: cartQuantities.get(String(variantId)) || 0,
          },
          resource: cart,
        },
      })
    );
  };

  const getRows = (variantId) =>
    document.querySelectorAll(`.elagili-card-buy-row[data-variant-id="${CSS.escape(String(variantId))}"]`);

  const renderRow = (row) => {
    const variantId = String(row.dataset.variantId || '');
    if (!variantId) return;

    const available = row.dataset.available === 'true';
    const qty = cartQuantities.get(variantId) || 0;
    const busy = busyVariants.has(variantId);
    const control = row.querySelector('.elagili-card-control');
    if (!control) return;

    if (!available) {
      control.innerHTML = '<span class="elagili-card-soldout">Sold out</span>';
      return;
    }

    if (qty > 0) {
      control.innerHTML = `
        <div class="elagili-card-qty">
          <button type="button" class="elagili-card-minus" aria-label="Decrease quantity" ${busy ? 'disabled' : ''}>−</button>
          <span class="elagili-card-count">${qty}</span>
          <button type="button" class="elagili-card-plus" aria-label="Increase quantity" ${busy ? 'disabled' : ''}>+</button>
        </div>
      `;
      return;
    }

    control.innerHTML = `<button type="button" class="elagili-card-add" ${busy ? 'disabled' : ''}>Add</button>`;
  };

  const renderVariant = (variantId) => {
    getRows(variantId).forEach(renderRow);
  };

  const renderAll = () => {
    document.querySelectorAll('.elagili-card-buy-row[data-variant-id]').forEach(renderRow);
  };

  const addVariant = async (variantId) => {
    variantId = String(variantId);
    if (!variantId || busyVariants.has(variantId)) return;

    busyVariants.add(variantId);
    renderVariant(variantId);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      if (!response.ok) throw new Error(`Add failed: HTTP ${response.status}`);

      const cart = await fetchCart();
      emitCartUpdate(cart, variantId);
    } catch (error) {
      console.error('Elagili native card add failed', error);
      try {
        await fetchCart();
      } catch (_) {}
    } finally {
      busyVariants.delete(variantId);
      renderVariant(variantId);
    }
  };

  const changeVariantQuantity = async (variantId, quantity) => {
    variantId = String(variantId);
    quantity = Math.max(0, Number(quantity) || 0);
    if (!variantId || busyVariants.has(variantId)) return;

    busyVariants.add(variantId);
    renderVariant(variantId);

    try {
      if (!cartLineKeys.has(variantId)) await fetchCart();
      const lineKey = cartLineKeys.get(variantId);
      if (!lineKey) throw new Error('Cart line key not found');

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: lineKey, quantity }),
      });

      if (!response.ok) throw new Error(`Quantity change failed: HTTP ${response.status}`);

      const cart = await response.json();
      rebuildCartMaps(cart);
      emitCartUpdate(cart, variantId);
    } catch (error) {
      console.error('Elagili native card quantity update failed', error);
      try {
        await fetchCart();
      } catch (_) {}
    } finally {
      busyVariants.delete(variantId);
      renderVariant(variantId);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('.elagili-card-add, .elagili-card-plus, .elagili-card-minus');
      if (!button) return;

      const row = button.closest('.elagili-card-buy-row');
      const variantId = row?.dataset.variantId;
      if (!variantId) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const current = cartQuantities.get(String(variantId)) || 0;

      if (button.classList.contains('elagili-card-add')) {
        addVariant(variantId);
      } else if (button.classList.contains('elagili-card-plus')) {
        changeVariantQuantity(variantId, current + 1);
      } else if (button.classList.contains('elagili-card-minus')) {
        changeVariantQuantity(variantId, current - 1);
      }
    },
    true
  );

  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === 'elagili-native-card-cart') return;
    fetchCart().then(renderAll).catch(() => {});
  });

  document.addEventListener('shopify:section:load', () => {
    fetchCart().then(renderAll).catch(() => {});
  });

  window.addEventListener('pageshow', () => {
    fetchCart().then(renderAll).catch(() => {});
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => fetchCart().then(renderAll).catch(() => {}),
      { once: true }
    );
  } else {
    fetchCart().then(renderAll).catch(() => {});
  }
})();

(() => {
  if (window.__elagiliBackInStockLoaded) return;
  window.__elagiliBackInStockLoaded = true;

  const styleId = 'elagili-back-in-stock-styles';

  const injectStyles = () => {
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .elagili-notify-btn{border:1px solid #354557;border-radius:999px;background:#fff;color:#354557;min-height:36px;padding:7px 13px;font:inherit;font-weight:600;line-height:1;cursor:pointer;white-space:nowrap;position:relative;z-index:45}
      .elagili-notify-btn:hover{background:#354557;color:#fff}
      .elagili-notify-btn--product{width:100%;min-height:46px;margin:10px 0 12px}
      .elagili-notify-overlay{position:fixed;z-index:150;inset:0;background:rgba(20,27,34,.42);display:none;align-items:center;justify-content:center;padding:18px}
      .elagili-notify-overlay.is-open{display:flex}
      .elagili-notify-modal{width:min(460px,100%);background:#fff;border-radius:18px;padding:22px;box-shadow:0 18px 60px rgba(0,0,0,.22);position:relative}
      .elagili-notify-close{position:absolute;top:10px;right:12px;border:0;background:transparent;color:#354557;font-size:30px;line-height:1;cursor:pointer}
      .elagili-notify-modal h3{margin:0 36px 8px 0;color:#263441}
      .elagili-notify-modal p{margin:0 0 14px;color:#66727d}
      .elagili-notify-form{display:grid;gap:10px}
      .elagili-notify-form input[type=email]{width:100%;min-height:46px;border:1px solid #cfd6dc;border-radius:10px;padding:10px 12px;font:inherit}
      .elagili-notify-submit{min-height:46px;border:0;border-radius:999px;background:#354557;color:#fff;padding:10px 16px;font:inherit;font-weight:700;cursor:pointer}
      .elagili-notify-submit:disabled{opacity:.6;cursor:wait}
      .elagili-notify-status{font-size:14px;min-height:20px}
      .elagili-notify-status.is-success{color:#19703b}
      .elagili-notify-status.is-error{color:#b42318}
      @media(max-width:749px){.elagili-notify-btn{min-height:34px;padding:6px 10px;font-size:12px}}
    `;
    document.head.appendChild(style);
  };

  const getCardProductData = (row) => {
    const card = row.closest('product-card');
    if (!card) return null;
    const link = card.querySelector('.product-card__link[href]');
    const title = card.querySelector('.visually-hidden')?.textContent?.trim() || 'Product';
    return {
      productId: String(card.dataset.productId || ''),
      variantId: String(row.dataset.variantId || ''),
      title,
      url: link?.href || window.location.href
    };
  };

  const getProductPageData = () => {
    const productForm = document.querySelector('main product-form-component[data-product-id]');
    const variantInput = productForm?.querySelector('input[name="id"]');
    const title = document.querySelector('meta[property="og:title"]')?.content || document.querySelector('main h1')?.textContent?.trim() || document.title;
    return {
      productId: String(productForm?.dataset?.productId || ''),
      variantId: String(variantInput?.value || ''),
      title,
      url: window.location.href
    };
  };

  const openModal = (data) => {
    const overlay = document.querySelector('.elagili-notify-overlay');
    if (!overlay) return;
    overlay.dataset.productId = data.productId || '';
    overlay.dataset.variantId = data.variantId || '';
    overlay.dataset.productTitle = data.title || '';
    overlay.dataset.productUrl = data.url || '';
    overlay.querySelector('.elagili-notify-product-name').textContent = data.title || 'this product';
    overlay.querySelector('.elagili-notify-status').textContent = '';
    overlay.querySelector('.elagili-notify-status').className = 'elagili-notify-status';
    overlay.querySelector('input[type=email]').value = '';
    overlay.classList.add('is-open');
    setTimeout(() => overlay.querySelector('input[type=email]')?.focus(), 0);
  };

  const createModal = () => {
    if (document.querySelector('.elagili-notify-overlay')) return;
    const overlay = document.createElement('div');
    overlay.className = 'elagili-notify-overlay';
    overlay.innerHTML = `
      <div class="elagili-notify-modal" role="dialog" aria-modal="true" aria-label="Back in stock notification">
        <button type="button" class="elagili-notify-close" aria-label="Close">×</button>
        <h3>Notify me when available</h3>
        <p>Enter your email and we’ll record your request for <strong class="elagili-notify-product-name"></strong>.</p>
        <form class="elagili-notify-form">
          <input type="email" name="email" placeholder="Email address" required autocomplete="email">
          <button type="submit" class="elagili-notify-submit">Notify me</button>
          <div class="elagili-notify-status" aria-live="polite"></div>
        </form>
      </div>`;
    document.body.appendChild(overlay);

    const close = () => overlay.classList.remove('is-open');
    overlay.querySelector('.elagili-notify-close').addEventListener('click', close);
    overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });

    overlay.querySelector('.elagili-notify-form').addEventListener('submit', async (event) => {
      event.preventDefault();
      const form = event.currentTarget;
      const email = form.email.value.trim();
      const submit = form.querySelector('.elagili-notify-submit');
      const status = form.querySelector('.elagili-notify-status');
      if (!email) return;

      submit.disabled = true;
      status.className = 'elagili-notify-status';
      status.textContent = 'Saving your request…';

      const body = new FormData();
      body.append('form_type', 'contact');
      body.append('utf8', '✓');
      body.append('contact[email]', email);
      body.append('contact[subject]', 'Back in stock request');
      body.append('contact[body]', `Back in stock request\nProduct: ${overlay.dataset.productTitle || ''}\nProduct ID: ${overlay.dataset.productId || ''}\nVariant ID: ${overlay.dataset.variantId || ''}\nURL: ${overlay.dataset.productUrl || ''}\nCustomer email: ${email}`);

      try {
const response = await fetch('/apps/elagili-tracking/back-in-stock', {
  method: 'POST',
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  body: JSON.stringify({
    email,
    product_id: overlay.dataset.productId || '',
    variant_id: overlay.dataset.variantId || '',
    product_title: overlay.dataset.productTitle || '',
    product_url: overlay.dataset.productUrl || ''
  })
});
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        status.className = 'elagili-notify-status is-success';
        status.textContent = 'Done — your request has been recorded.';
        submit.textContent = 'Recorded';
        setTimeout(() => { close(); submit.textContent = 'Notify me'; submit.disabled = false; }, 1400);
      } catch (error) {
        console.error('Elagili back in stock request failed', error);
        status.className = 'elagili-notify-status is-error';
        status.textContent = 'Could not save your request. Please try again.';
        submit.disabled = false;
      }
    });
  };

  const enhanceSoldOutCards = (root = document) => {
    root.querySelectorAll?.('.elagili-card-buy-row[data-available="false"]').forEach((row) => {
      const control = row.querySelector('.elagili-card-control');
      if (!control) return;
      if (control.querySelector('.elagili-notify-btn')) return;
      const data = getCardProductData(row);
      control.innerHTML = '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'elagili-notify-btn';
      button.textContent = 'Notify me';
      button.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); if (data) openModal(data); });
      control.appendChild(button);
    });
  };

  const getProductAddButton = () => {
  return (
    document.querySelector('main .buy-buttons-block button[type="submit"]') ||
    document.querySelector('main .buy-buttons-block .add-to-cart-button') ||
    document.querySelector('main product-form-component button[name="add"]') ||
    document.querySelector('main product-form button[name="add"]') ||
    document.querySelector('main form[action*="/cart/add"] button[type="submit"]') ||
    document.querySelector('main form[data-type="add-to-cart-form"] button[type="submit"]') ||
    document.querySelector('main button[id*="ProductSubmitButton"]') ||
    document.querySelector('main button[name="add"]')
  );
};

const productPageIsSoldOut = () => {
  if (!/\/products\//.test(window.location.pathname)) return false;

  const addButton = getProductAddButton();

  if (addButton) {
    const text = (addButton.textContent || '').trim().toLowerCase();

    if (
      addButton.disabled ||
      addButton.hasAttribute('disabled') ||
      addButton.getAttribute('aria-disabled') === 'true' ||
      text.includes('sold out') ||
      text.includes('unavailable')
    ) {
      return true;
    }
  }

  const productArea =
    document.querySelector('main product-form-component') ||
    document.querySelector('main product-form') ||
    document.querySelector('main .buy-buttons-block') ||
    document.querySelector('main form[action*="/cart/add"]');

  if (productArea) {
    const text = (productArea.textContent || '').toLowerCase();

    if (
      text.includes('sold out') ||
      text.includes('unavailable')
    ) {
      return true;
    }
  }

  return false;
};

const enhanceProductPage = () => {
  if (!/\/products\//.test(window.location.pathname)) return;

  const existing = document.querySelector('.elagili-notify-btn--product');
  const soldOut = productPageIsSoldOut();

  if (!soldOut) {
    existing?.remove();
    return;
  }

  if (existing) return;

  const addButton = getProductAddButton();

  const productForm =
    document.querySelector('main product-form-component[data-product-id]') ||
    document.querySelector('main product-form-component') ||
    document.querySelector('main product-form') ||
    document.querySelector('main form[action*="/cart/add"]');

  const buyBlock =
    document.querySelector('main .buy-buttons-block') ||
    addButton?.parentElement ||
    productForm;

  if (!buyBlock) return;

  const button = document.createElement('button');

  button.type = 'button';
  button.className = 'elagili-notify-btn elagili-notify-btn--product';
  button.textContent = 'Notify me when available';

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    openModal(getProductPageData());
  });

  buyBlock.insertAdjacentElement('afterend', button);
};

  const run = (root = document) => {
    injectStyles();
    createModal();
    enhanceSoldOutCards(root);
    enhanceProductPage();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document), { once: true });
  else run(document);

  document.addEventListener('shopify:section:load', (event) => run(event.target || document));
  document.addEventListener('change', () => setTimeout(() => enhanceProductPage(), 80), true);

  const observer = new MutationObserver(() => {
    enhanceSoldOutCards(document);
    enhanceProductPage();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
})();